'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { PencilGlyph } from '../../../internal/icons.js';
import { Button } from '../../actions/button/index.js';
import { Spinner } from '../../feedback/spinner/index.js';
// useLayoutEffect warns during server rendering; focus work only runs in the browser.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
function defaultMessage(error) {
    if (error instanceof Error && error.message)
        return error.message;
    return 'Couldn’t save. Try again.';
}
/**
 * Text that turns into an input in place: click it or press Enter to edit,
 * Enter (or leaving the field) saves, Escape cancels. Saving can be async,
 * with a pending state, and a failed save keeps the editor open with the
 * error. The product owns persistence and conflict handling.
 */
export const InlineEditText = forwardRef(function InlineEditText({ label, value: valueProp, defaultValue = '', onSave, onCancel, onSaveError, getErrorMessage = defaultMessage, validate, error: externalError, editing: editingProp, defaultEditing = false, onEditingChange, placeholder, multiline = false, rows = 3, maxLength, trim = true, saveOnBlur = true, showActions = multiline, disabled = false, editHint = 'Edit', saveLabel = 'Save', cancelLabel = 'Cancel', savingLabel = 'Saving', className, ...rest }, ref) {
    const auto = useId();
    const errorId = `mn-inline-edit-${auto}-error`;
    const hintId = `mn-inline-edit-${auto}-hint`;
    const [innerValue, setInnerValue] = useState(defaultValue);
    const value = valueProp ?? innerValue;
    const [innerEditing, setInnerEditing] = useState(defaultEditing && !disabled);
    const editing = (editingProp ?? innerEditing) && !disabled;
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const [localError, setLocalError] = useState(null);
    const viewRef = useRef(null);
    const inputRef = useRef(null);
    const rootRef = useRef(null);
    // Focus returns to the view button after Enter / Escape / the buttons, not
    // after a blur save (the user has already moved elsewhere).
    const restoreFocus = useRef(false);
    const failedDraft = useRef(null);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const setEditing = useCallback((next) => {
        if (editingProp === undefined)
            setInnerEditing(next);
        onEditingChange?.(next);
    }, [editingProp, onEditingChange]);
    // Entering edit mode starts the draft from the saved value and focuses
    // it; leaving after Enter / Escape / the buttons returns focus to the text.
    const valueRef = useRef(value);
    valueRef.current = value;
    const wasEditing = useRef(editing);
    useIsomorphicLayoutEffect(() => {
        if (editing && !wasEditing.current) {
            setDraft(valueRef.current);
            setLocalError(null);
            failedDraft.current = null;
            inputRef.current?.focus();
            inputRef.current?.select();
        }
        if (!editing && wasEditing.current && restoreFocus.current) {
            viewRef.current?.focus();
        }
        if (!editing)
            restoreFocus.current = false;
        wasEditing.current = editing;
    }, [editing]);
    // Mounted already editing (defaultEditing / editing): focus the input once.
    useEffect(() => {
        if (wasEditing.current)
            inputRef.current?.focus();
    }, []);
    const exit = (restore) => {
        restoreFocus.current = restore;
        setLocalError(null);
        failedDraft.current = null;
        setEditing(false);
    };
    const commit = async (restore) => {
        if (saving)
            return;
        const next = trim ? draft.trim() : draft;
        if (next === value) {
            exit(restore);
            return;
        }
        const invalid = validate?.(next);
        if (invalid !== null && invalid !== undefined && invalid !== false && invalid !== '') {
            setLocalError(invalid);
            failedDraft.current = draft;
            return;
        }
        setSaving(true);
        setLocalError(null);
        try {
            await onSave(next);
            if (!mounted.current)
                return;
            if (valueProp === undefined)
                setInnerValue(next);
            setSaving(false);
            exit(restore);
        }
        catch (err) {
            if (!mounted.current)
                return;
            setSaving(false);
            failedDraft.current = draft;
            setLocalError(getErrorMessage(err));
            onSaveError?.(err, next);
            // Keep the user in the editor with their draft.
            if (restore)
                inputRef.current?.focus();
        }
    };
    const cancel = () => {
        if (saving)
            return;
        setDraft(value);
        exit(true);
        onCancel?.();
    };
    const onKeyDown = (event) => {
        if (event.nativeEvent.isComposing)
            return;
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            cancel();
        }
        else if (event.key === 'Enter' && (!multiline || event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void commit(true);
        }
    };
    const onBlur = (event) => {
        if (!saveOnBlur || saving)
            return;
        // Moving to the Save / Cancel buttons is not leaving the editor.
        if (event.relatedTarget && rootRef.current?.contains(event.relatedTarget))
            return;
        // Do not retry a draft that has just failed; the error stays visible.
        if (failedDraft.current !== null && failedDraft.current === draft)
            return;
        void commit(false);
    };
    const shownError = localError ?? (editing ? externalError : null);
    const hasError = shownError !== null && shownError !== undefined && shownError !== false && shownError !== '';
    const empty = value === '';
    const setRoot = (el) => {
        rootRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    };
    const shared = {
        'aria-label': label,
        'aria-invalid': hasError || undefined,
        'aria-describedby': hasError ? errorId : undefined,
        'aria-busy': saving || undefined,
        readOnly: saving,
        value: draft,
        placeholder,
        maxLength,
        onKeyDown,
        onBlur,
        className: 'mn-inline-edit-text__input',
    };
    return (_jsxs("div", { ref: setRoot, ...rest, className: cx('mn-inline-edit-text', className), "data-state": editing ? 'editing' : 'viewing', "data-multiline": multiline || undefined, "data-empty": empty || undefined, "data-saving": saving || undefined, "data-invalid": hasError || undefined, "data-disabled": disabled || undefined, children: [editing ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mn-inline-edit-text__editor", children: [multiline ? (_jsx("textarea", { ref: inputRef, ...shared, rows: rows, onChange: (e) => setDraft(e.target.value) })) : (_jsx("input", { ref: inputRef, ...shared, type: "text", onChange: (e) => setDraft(e.target.value) })), saving && !showActions ? (_jsx(Spinner, { size: "sm", label: savingLabel, className: "mn-inline-edit-text__spinner" })) : null] }), hasError ? (_jsx("p", { id: errorId, role: "alert", className: "mn-inline-edit-text__error", children: shownError })) : null, showActions ? (_jsxs("div", { className: "mn-inline-edit-text__actions", children: [_jsx(Button, { size: "sm", variant: "primary", loading: saving, onMouseDown: (e) => e.preventDefault(), onClick: () => void commit(true), children: saving ? savingLabel : saveLabel }), _jsx(Button, { size: "sm", variant: "ghost", disabled: saving, onMouseDown: (e) => e.preventDefault(), onClick: cancel, children: cancelLabel })] })) : null] })) : disabled ? (_jsx("span", { className: "mn-inline-edit-text__view", children: _jsx("span", { className: "mn-inline-edit-text__value", children: empty ? placeholder : value }) })) : (_jsxs("button", { ref: viewRef, type: "button", className: "mn-inline-edit-text__view", "aria-describedby": hintId, onClick: () => setEditing(true), children: [_jsxs("span", { className: "mn-visually-hidden", children: [label, ": "] }), _jsx("span", { className: "mn-inline-edit-text__value", children: empty ? (placeholder ?? '\u00a0') : value }), _jsx(PencilGlyph, { className: "mn-inline-edit-text__icon" })] })), !editing && !disabled ? (_jsx("span", { id: hintId, className: "mn-visually-hidden", children: editHint })) : null] }));
});
//# sourceMappingURL=inline-edit-text.js.map